import React, { useMemo, Fragment } from 'react'
import { isEmpty } from 'lodash'
import { Field, useFormikContext } from 'formik'
import PropTypes from 'prop-types'

import Checkbox from 'components/Checkbox'

import { calculateDiscount, getItemsWithSpecialType } from 'utils/Refund'
import { formatCentsToUSD } from 'utils/currency'

const RefundItems = ({ order, disabled, itemsWithoutSpecialType }) => {
  const { lineItems, promotions, tip } = order

  const { values, setFieldValue } = useFormikContext()

  // line items with special type are items which does not have checboxes, there are just
  // items for display and calculate total refund ex. Tax
  const lineItemsWithSpecialType = getItemsWithSpecialType(lineItems)

  const checkBoxLabel = (quantity, name) => `+(${quantity}) ${name}`

  const discountInCents = useMemo(
    () => calculateDiscount(promotions),
    [promotions]
  )

  // return modifiersUuid of current main item
  const modifiersUuid = (uuid) => {
    // filter last selected main item
    const lineItem = itemsWithoutSpecialType.filter(
      (item) => item.uuid === uuid
    )

    // if item has modifiers, add those modifiers uuid into lineItemsUuid
    if (!isEmpty(lineItem[0]?.modifiers)) {
      return lineItem[0]?.modifiers[0].map((modifier) => modifier.uuid)
    }
  }

  const onChangeMainCheckbox = (event, uuid) => {
    if (event.target.checked) {
      // new Set in this case eliminate duplicates in array
      const newLineItemsUuid = [
        ...new Set([...values.lineItemUuids, ...modifiersUuid(uuid), uuid]),
      ]

      setFieldValue('lineItemUuids', newLineItemsUuid)
    } else {
      // this filter return items that was not checked and also if any last checked
      // main item had his modifiers, those modifiers are also removed from an array
      const filterMainItemsAndModifiers = values?.lineItemUuids.filter(
        (val) => ![uuid, ...modifiersUuid(uuid)].includes(val)
      )

      setFieldValue('lineItemUuids', filterMainItemsAndModifiers)
    }
  }

  const onChangeModifierCheckbox = (event, uuid) => {
    if (event.target.checked) {
      // add last checked modifier to  line items
      const newLineItemsUuid = [...values.lineItemUuids, uuid]

      setFieldValue('lineItemUuids', newLineItemsUuid)
    } else {
      // array of line items without last uncheck modifier
      const filteredUuids = values?.lineItemUuids.filter((val) => val !== uuid)

      setFieldValue('lineItemUuids', filteredUuids)
    }
  }

  return (
    <div className="refund-items">
      <div className="refund-items__details">
        {itemsWithoutSpecialType.map(
          ({ name, price, modifiers, uuid, quantity }) => {
            return (
              <Fragment key={uuid}>
                <div className="refund-items__main">
                  <Field
                    component={Checkbox}
                    checked={values?.lineItemUuids?.includes(uuid)}
                    onChange={(e) => onChangeMainCheckbox(e, uuid)}
                    label={checkBoxLabel(quantity, name)}
                    disabled={disabled}
                  />
                  <p className="refund-items__amount">${price}</p>
                </div>
                {!isEmpty(modifiers) &&
                  modifiers[0]?.map(
                    ({ uuid, quantity: modifierQuantity, name, price }) => {
                      return (
                        <div className="refund-items__modifier" key={uuid}>
                          <Field
                            component={Checkbox}
                            checked={values?.lineItemUuids?.includes(uuid)}
                            label={checkBoxLabel(modifierQuantity, name)}
                            onChange={(e) =>
                              onChangeModifierCheckbox(e, uuid ?? '')
                            }
                            disabled={disabled}
                          ></Field>
                          <p className="refund-items__amount">${price ?? 0}</p>
                        </div>
                      )
                    }
                  )}
              </Fragment>
            )
          }
        )}
      </div>
      {!isEmpty(promotions) && (
        <div className="refund-items__row -disabled">
          <span>Discount</span>
          <span>{formatCentsToUSD(discountInCents)}</span>
        </div>
      )}
      {lineItemsWithSpecialType?.map(({ name, price }, index) => {
        return (
          <div className="refund-items__row -disabled" key={index}>
            <span>{name}</span>
            <span>${price}</span>
          </div>
        )
      })}
      {!isEmpty(tip) && (
        <div className="refund-items__row -disabled">
          <span>Gratuity</span>
          <span>${tip}</span>
        </div>
      )}
    </div>
  )
}

RefundItems.defaultProps = {
  order: {},
  disabled: false,
}

RefundItems.propTypes = {
  order: PropTypes.object,
  disabled: PropTypes.bool,
  itemsWithoutSpecialType: PropTypes.array,
}

export default RefundItems
